/**
 * @module ComponentManager
 */

import Component from "./component.js";

/**
 * General component manager. Holds a series of components,
 * and updates them when required.
 * 
 * The assumed update process is per-scene.
 * First, all `preUpdate` methods are run, starting with the scene and
 * proceeding to all its children in order.
 * Second, all `update` methods are run in the same order.
 * Finally, all `postUpdate` methods are run.
 * Thus, you can safely assume that `postUpdate` will have access to
 * all updates made during `update`.
 * 
 * @class ComponentManager
 */

const ComponentManager = class {
    constructor ( owner ) {

        /**
         * List of components available on this manager.
         * Use the public `components` object to examine this data.
         * @private
         */
        this._components = [];

        /**
         * Owner of this manager.
         * @type {Phaser.Scene|Phaser.GameObjects.GameObject}
         */
        this.owner = owner;
    }

    /**
     * List of components available on this manager.
     * @property {Array} components
     * @readonly
     */
    get components() {
        return this._components.slice();
    }

    /**
     * Game in which this manager runs.
     * This is a dynamic getter, in case the owner migrates between scenes.
     * @property {Phaser.Game} game
     * @readonly
     */
    get game() {
        const scene = this.scene;
        if ( scene ) {
            return this.scene.game;
        } else {
            return null;
        }
    }

    /**
     * Scene in which this manager runs.
     * This is a dynamic getter, in case the owner migrates between scenes.
     * @property {Phaser.Scene} scene
     * @readonly
     */
    get scene() {
        if ( !this.owner ) {
            return null;
        } else if ( this.owner instanceof Phaser.Scene ) {
            return this.owner;
        } else {
            return this.owner.scene;
        }
    }

    /**
     * Add a component to this manager.
     * Ensure that its `owner` and `manager` are set appropriately.
     * @param {Component} component Component to add
     */
    add( component ) {
        this.addAt( component, this._components.length );
    }

    /**
     * Add a component to this manager, at the indicated index.
     * Ensure that its `owner` and `manager` are set appropriately.
     * 
     * This is a splice operation and the index obeys splice rules,
     * so a negative index counts back from the end of the component list,
     * and an index larger than the list length stops at the end of the list.
     * 
     * @param {Component} component - component to add
     * @param {Number} index - index at which to insert component
     */
    addAt( component, index ) {
        component.owner = this.owner;
        this._components.splice( index, 0, component );
    }

    /**
     * Create and add a `Component` to this manager.
     * @param {Object} config - composite parameter object for `Component`
     * @returns {Component} created component
     */
    create( config ) {
        config = config || {};
        const component = new Component( config );
        this.add( component );
        return component;
    }

    /**
     * Return the component or components matching the given name.
     * The return is always a list, suitable for iteration
     * even when zero matches were found.
     * @param {string} name - component name to find
     * @returns {Array}
     */
    getByName( name ) {
        const out = [];
        this._components.forEach( component => {
            if ( component.name === name ) {
                out.push( component );
            }
        } );
        return out;
    }

    /**
     * Remove a component from this manager. It will cease to update.
     * @param {Component} component - component to remove
     */
    remove( component ) {
        const index = this._components.indexOf( component );
        this.removeAt( index );
    }

    /**
     * Remove all components from this manager.
     */
    removeAll() {
        for ( let i = this._components.length - 1; i >= 0; i-- ) {
            this.removeAt( i );
        }
    }

    /**
     * Remove the component at the index. This is the core removal method.
     * @param {Number} index - location of removal target
     */
    removeAt( index ) {
        const component = this._components[ index ];
        if ( component ) {
            if ( component.destroy ) {
                component.destroy();
            }
            component.manager = null;
            component.owner = null;
            this._components.splice( index, 1 );
        }
    }

    /**
     * Remove all components with this name. They will cease to update.
     * @param {String} name - name of components to remove
     * @returns {Number} - number of components removed
     */
    removeByName( name ) {
        let removed = 0;
        for ( let i = this._components.length - 1; i >= 0; i-- ) {
            const component = this._components[ i ];
            if ( component.name === name ) {
                this.removeAt( i );
                removed++;
            }
        }
        return removed;
    }

    /**
     * Pre-update all components.
     * @param {Number} time - current game time
     * @param {Number} delta - current frame delta
     */
    preUpdate( time, delta ) {
        this._components.forEach( component => {
            if ( component.active ) {
                component.preUpdate( time, delta );
            }
        } );
    }

    /**
     * Update all components.
     * @param {Number} time - current game time
     * @param {Number} delta - current frame delta
     */
    update( time, delta ) {
        this._components.forEach( component => {
            if ( component.active ) {
                component.update( time, delta );
            }
        } );
    }

    /**
     * Post-update all components.
     * @param {Number} time - current game time
     * @param {Number} delta - current frame delta
     */
    postUpdate( time, delta ) {
        this._components.forEach( component => {
            if ( component.active ) {
                component.postUpdate( time, delta );
            }
        } );
    }
};

export default ComponentManager;
